/**
 * CAT Edge admin template — single source of truth for ALL consumer-facing
 * customization. Edit this file to rebrand, change navigation, swap routes,
 * toggle tenancy, or add new quick actions / bottom tabs.
 *
 * The template's chrome (Header, Sidebar, MobileBottomTabs, AppLayout) reads
 * everything it needs from this object.
 */

import {
  BookOpen,
  Building2,
  CreditCard,
  Database,
  FolderKanban,
  LayoutDashboard,
  Mail,
  Search,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { SidebarItem } from './lib/sidebar';

export interface BrandConfig {
  /** Long form used in the user pill subtitle, OAuth screens, footer, etc. */
  name: string;
  /** One-character fallback mark used when no logo asset is configured. */
  shortName: string;
  /** Optional brand image rendered in the shell header/sidebar. */
  logoSrc?: string;
  /** Optional alt text override for the brand image. */
  logoAlt?: string;
  /** Version label rendered next to the wordmark (e.g., 'v0.3 · beta'). */
  versionLabel?: string;
  /** Accent color override (any valid CSS color). Default: var(--ce-color-accent). */
  accentColor?: string;
}

export interface QuickAction {
  label: string;
  to: string;
}

export interface BottomTab {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Destination path. Omit for action-style tabs. */
  to?: string;
  /** Action mode: 'menu' opens the drawer; 'search' focuses the header search input. */
  action?: 'menu' | 'search';
}

export interface RoutesConfig {
  /** Where unauthenticated users are sent. */
  login: string;
  /** Default landing path used by logo links. */
  dashboard: string;
  /** Where the Docs icon in the header points. Omit to hide it. */
  docs?: string;
  /** Where the user pill clicks through to (account settings). Optional. */
  account?: string;
}

export interface TemplateConfig {
  brand: BrandConfig;
  /** Toggle the workspace switcher in the Sidebar and the tenant pill in the Header. */
  tenancy: 'enabled' | 'disabled';
  /** Role string treated as "admin" for adminOnly sidebar entries. Default: 'admin'. */
  adminRole?: string;
  /** Top-of-app primary navigation tree. Use SidebarItem with `adminOnly` / `roles` to scope. */
  navigation: SidebarItem[];
  /** "Jump to:" chips rendered under the header search bar. */
  quickActions: QuickAction[];
  /** Mobile-only bottom tab bar. Recommended: 4 tabs (Home / Projects / Search / Menu). */
  bottomTabs: BottomTab[];
  routes: RoutesConfig;
  /** Environment label shown in the status pill (development/staging/production/etc). */
  appEnv?: string;
}

/**
 * Default config — change this for your project. The example navigation
 * below mirrors the CAT Edge sidebar so the template renders something
 * useful out of the box.
 */
export const templateConfig: TemplateConfig = {
  brand: {
    name: 'CAT Edge',
    shortName: 'C',
    versionLabel: 'v0.3 · beta',
  },
  tenancy: 'enabled',
  adminRole: 'admin',
  navigation: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/app/dashboard',
    },
    {
      id: 'workspace',
      label: 'Workspace',
      icon: FolderKanban,
      children: [{ id: 'projects', label: 'Projects', icon: FolderKanban, path: '/app/workspace' }],
    },
    {
      id: 'assets',
      label: 'Linguistic assets',
      icon: Database,
      children: [
        { id: 'tm', label: 'Translation memory', icon: Database, path: '/app/assets/tm' },
        { id: 'tb', label: 'Termbases', icon: BookOpen, path: '/app/assets/tb/view' },
      ],
    },
    {
      id: 'settings',
      label: 'System Settings',
      icon: Settings,
      children: [
        { id: 'ws', label: 'Workspace & Team', icon: Building2, path: '/app/settings/workspace' },
        { id: 'prefs', label: 'Preferences', icon: Settings, path: '/app/settings/preferences' },
      ],
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: ShieldCheck,
      adminOnly: true,
      children: [
        {
          id: 'overview',
          label: 'Overview',
          icon: ShieldCheck,
          path: '/app/admin/overview',
          adminOnly: true,
        },
        { id: 'users', label: 'Users', icon: Users, path: '/app/admin/users', adminOnly: true },
        {
          id: 'billing',
          label: 'Billing',
          icon: CreditCard,
          path: '/app/billing',
          adminOnly: true,
        },
        {
          id: 'notif',
          label: 'Notifications',
          icon: Mail,
          path: '/app/admin/notifications',
          adminOnly: true,
        },
      ],
    },
  ],
  quickActions: [
    { label: 'Dashboard', to: '/app/dashboard' },
    { label: 'Projects', to: '/app/workspace' },
    { label: 'Translation memory', to: '/app/assets/tm' },
    { label: 'Settings', to: '/app/settings/preferences' },
  ],
  bottomTabs: [
    { id: 'dash', label: 'Home', icon: LayoutDashboard, to: '/app/dashboard' },
    { id: 'proj', label: 'Projects', icon: FolderKanban, to: '/app/workspace' },
    { id: 'search', label: 'Search', icon: Search, action: 'search' },
    { id: 'menu', label: 'Menu', icon: Settings, action: 'menu' },
  ],
  routes: {
    login: '/login',
    dashboard: '/app/dashboard',
    docs: '/docs',
  },
  appEnv: 'development',
};
